import { useMemo } from "react";
import {
  parseISO,
  startOfDay,
  startOfMonth,
  format,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  subDays,
} from "date-fns";
import { useQuery } from "@tanstack/react-query";
import api from "@/utils/api";
import useDashboardFilters from "@/hooks/useDashboardFilters";

const buildKey = (prefix, filters) => [
  prefix,
  filters.dateFrom,
  filters.dateTo,
  filters.projectIds.join(","),
  filters.customerIds.join(","),
  filters.status,
];

export default function useDashboardData(options = {}) {
  const { debouncedFilters, isHydrated } = useDashboardFilters();
  const {
    includeStatus = true,
    includeProjects = true,
    includeCustomers = true,
    includeTimeseries = true,
    includeForecast = true,
    timeseriesSource = null,
    timeseriesBreakdown = null,
    forecastScenario = "neutral",
    overviewSource = null,
    forceHistorical = false,
  } = options;

  const today = startOfDay(new Date());
  const todayLabel = format(today, "yyyy-MM-dd");


  const resolvedGranularity = useMemo(() => {
    if (debouncedFilters.granularity) {
      return debouncedFilters.granularity;
    }
    if (!debouncedFilters.dateFrom || !debouncedFilters.dateTo) {
      return "month";
    }
    const from = parseISO(debouncedFilters.dateFrom);
    const to = parseISO(debouncedFilters.dateTo);
    const dayCount = Math.max(0, differenceInCalendarDays(to, from));
    if (dayCount <= 45) {
      return "day";
    }
    if (dayCount <= 180) {
      return "week";
    }
    if (dayCount <= 730) {
      return "month";
    }
    return "quarter";
  }, [debouncedFilters.dateFrom, debouncedFilters.dateTo, debouncedFilters.granularity]);

  const rangeContext = useMemo(() => {
    const from = debouncedFilters.dateFrom ? parseISO(debouncedFilters.dateFrom) : null;
    const to = debouncedFilters.dateTo ? parseISO(debouncedFilters.dateTo) : null;

    if (!from || !to) {
      return { mode: "mixed", forecastStartLabel: format(today, "dd.MM.yyyy") };
    }

    if (to <= today) {
      return { mode: "historical", forecastStartLabel: null };
    }

    if (from > today) {
      return { mode: "future", forecastStartLabel: null };
    }

    const forecastStart = from > today ? from : today;
    return { mode: "mixed", forecastStartLabel: format(forecastStart, "dd.MM.yyyy") };
  }, [debouncedFilters.dateFrom, debouncedFilters.dateTo]);

  const effectiveDateTo = useMemo(() => {
    if (!debouncedFilters.dateTo) return null;
    if (rangeContext.mode === "mixed") return todayLabel;
    if (forceHistorical && rangeContext.mode === "future") return todayLabel;
    return debouncedFilters.dateTo;
  }, [debouncedFilters.dateTo, forceHistorical, rangeContext.mode, todayLabel]);

  const effectiveDateFrom = useMemo(() => {
    if (!debouncedFilters.dateFrom) return null;
    if (!forceHistorical || !effectiveDateTo) return debouncedFilters.dateFrom;
    const from = parseISO(debouncedFilters.dateFrom);
    const to = parseISO(effectiveDateTo);
    if (from > to) {
      return effectiveDateTo;
    }
    return debouncedFilters.dateFrom;
  }, [debouncedFilters.dateFrom, effectiveDateTo, forceHistorical]);

  const hasDateRange = Boolean(effectiveDateFrom && effectiveDateTo);
  const canFetch = isHydrated && hasDateRange;

  const shouldFetchHistorical = canFetch && (rangeContext.mode !== "future" || forceHistorical);
  const shouldFetchForecast = canFetch && rangeContext.mode === "mixed" && includeForecast && !forceHistorical;

  const baseParamsResolved = useMemo(() => {
    const params = {
      date_from: effectiveDateFrom,
      date_to: effectiveDateTo,
    };
    if (includeProjects && debouncedFilters.projectIds.length) {
      params.project_ids = debouncedFilters.projectIds;
    }
    if (includeCustomers && debouncedFilters.customerIds.length) {
      params.customer_ids = debouncedFilters.customerIds;
    }
    if (includeStatus && debouncedFilters.status && debouncedFilters.status !== "all") {
      params.status = debouncedFilters.status;
    }
    if (overviewSource) {
      params.source = overviewSource;
    }
    return params;
  }, [
    debouncedFilters.customerIds,
    debouncedFilters.projectIds,
    debouncedFilters.status,
    effectiveDateFrom,
    effectiveDateTo,
    includeCustomers,
    includeProjects,
    includeStatus,
    overviewSource,
  ]);

  const historicalParams = useMemo(() => {
    if (!effectiveDateTo) return baseParamsResolved;
    return { ...baseParamsResolved, date_to: effectiveDateTo };
  }, [baseParamsResolved, effectiveDateTo]);

  const timeseriesParams = useMemo(() => {
    const params = { ...historicalParams, granularity: resolvedGranularity };
    if (timeseriesSource) {
      params.source = timeseriesSource;
    }
    if (timeseriesBreakdown) {
      params.breakdown = timeseriesBreakdown;
    }
    return params;
  }, [historicalParams, timeseriesBreakdown, timeseriesSource, resolvedGranularity]);

  const comparisonInfo = useMemo(() => {
    const from = debouncedFilters.dateFrom ? parseISO(debouncedFilters.dateFrom) : null;
    const to = effectiveDateTo ? parseISO(effectiveDateTo) : null;
    if (!from || !to) {
      return { label: "Vorperiode", rangeLabel: "" };
    }
    const daySpan = differenceInCalendarDays(to, from);
    const prevEnd = subDays(from, 1);
    const prevStart = subDays(prevEnd, daySpan);

    const presetLabelMap = {
      "today": "Vortag",
      "this-week": "Vorwoche",
      "last-30-days": "Vorperiode",
      "this-month": "Vormonat",
      "this-quarter": "Vorquartal",
      "this-year": "Vorjahr",
      "custom": "Vorzeitraum",
    };
    const label = presetLabelMap[debouncedFilters.datePreset] || "Vorperiode";
    const rangeLabel = `${format(prevStart, "dd.MM.yyyy")}–${format(prevEnd, "dd.MM.yyyy")}`;
    return { label, rangeLabel };
  }, [debouncedFilters.dateFrom, debouncedFilters.datePreset, debouncedFilters.dateTo]);

  const forecastMonthsAhead = useMemo(() => {
    if (!debouncedFilters.dateTo) return 12;
    const todayMonth = startOfMonth(new Date());
    const endMonth = startOfMonth(parseISO(debouncedFilters.dateTo));
    const diff = differenceInCalendarMonths(endMonth, todayMonth);
    return Math.min(Math.max(diff + 1, 1), 24);
  }, [debouncedFilters.dateTo]);

  const scenarioMap = {
    conservative: "pessimistic",
    neutral: "realistic",
    offensive: "optimistic",
  };
  const scenarioParam = scenarioMap[forecastScenario] || "realistic";

  const overviewQuery = useQuery({
    queryKey: ["dashboard", "overview", historicalParams],
    queryFn: async () => {
      const response = await api.get("/dashboard/overview", {
        params: { ...historicalParams, compare_to: "previous_period" },
      });
      return response.data;
    },
    enabled: shouldFetchHistorical,
  });

  const revenueSeriesQuery = useQuery({
    queryKey: ["dashboard", "timeseries", "revenue", timeseriesParams],
    queryFn: async () => {
      const response = await api.get("/dashboard/timeseries", {
        params: { ...timeseriesParams, metric: "revenue" },
      });
      return response.data;
    },
    enabled: shouldFetchHistorical && includeTimeseries,
  });

  const expenseSeriesQuery = useQuery({
    queryKey: ["dashboard", "timeseries", "expenses", timeseriesParams],
    queryFn: async () => {
      const response = await api.get("/dashboard/timeseries", {
        params: { ...timeseriesParams, metric: "expenses" },
      });
      return response.data;
    },
    enabled: shouldFetchHistorical && includeTimeseries,
  });

  const netSeriesQuery = useQuery({
    queryKey: ["dashboard", "timeseries", "net", timeseriesParams],
    queryFn: async () => {
      const response = await api.get("/dashboard/timeseries", {
        params: { ...timeseriesParams, metric: "net" },
      });
      return response.data;
    },
    enabled: shouldFetchHistorical && includeTimeseries,
  });

  const forecastQuery = useQuery({
    queryKey: ["dashboard", "forecast", forecastScenario, ...buildKey("forecast", debouncedFilters)],
    queryFn: async () => {
      const response = await api.get("/dashboard/forecast", {
        params: { months_ahead: forecastMonthsAhead, scenario: scenarioParam },
      });
      return response.data;
    },
    enabled: shouldFetchForecast && includeForecast,
  });

  const chartData = useMemo(() => {
    const revenueSeries = revenueSeriesQuery.data?.data || [];
    const expenseSeries = expenseSeriesQuery.data?.data || [];
    const map = new Map();

    revenueSeries.forEach((point) => {
      map.set(point.date, {
        date: point.date,
        revenueTotal: point.value,
        revenueNet: point.net ?? point.value,
        revenueVat: point.vat ?? 0,
        expenseTotal: 0,
        expenseNet: 0,
        expenseVat: 0,
      });
    });
    expenseSeries.forEach((point) => {
      const existing = map.get(point.date) || {
        date: point.date,
        revenueTotal: 0,
        revenueNet: 0,
        revenueVat: 0,
        expenseTotal: 0,
        expenseNet: 0,
        expenseVat: 0,
      };
      existing.expenseTotal = point.value;
      existing.expenseNet = point.net ?? point.value;
      existing.expenseVat = point.vat ?? 0;
      map.set(point.date, existing);
    });

    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [revenueSeriesQuery.data, expenseSeriesQuery.data]);

  return {
    overview: overviewQuery.data,
    overviewLoading: overviewQuery.isLoading,
    overviewError: overviewQuery.error,
    revenueSeries: revenueSeriesQuery.data?.data || [],
    expenseSeries: expenseSeriesQuery.data?.data || [],
    netSeries: netSeriesQuery.data?.data || [],
    chartData,
    chartsLoading: revenueSeriesQuery.isLoading || expenseSeriesQuery.isLoading,
    chartsError: revenueSeriesQuery.error || expenseSeriesQuery.error,
    forecast: forecastQuery.data?.forecast || [],
    forecastTax: forecastQuery.data?.tax_estimate || null,
    forecastRecommendations: forecastQuery.data?.recommendations || [],
    forecastLoading: forecastQuery.isLoading,
    forecastError: forecastQuery.error,
    topProjects: overviewQuery.data?.top_projects || [],
    rangeMode: rangeContext.mode,
    forecastStartLabel: rangeContext.forecastStartLabel,
    comparisonLabel: comparisonInfo.label,
    comparisonRangeLabel: comparisonInfo.rangeLabel,
    forecastMonthsAhead,
    rangeStart: debouncedFilters.dateFrom,
    rangeEnd: debouncedFilters.dateTo,
  };
}